import { useEffect, useRef, useState } from "react";
import { api, eur, dateFr } from "@/lib/rentApi";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Upload, TrendingUp, TrendingDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EXP_CATS = ["Travaux", "Charges de copropriété", "Assurance", "Taxe foncière", "Frais de gestion", "Intérêts d'emprunt", "Honoraires comptable", "Autre"];
const REV_CATS = ["Loyer", "Charges récupérées", "Dépôt de garantie", "Autre"];

export default function Accounting() {
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), type: "expense", category: "Travaux", amount: "", property_id: "", description: "" });
  const fileRef = useRef();

  const load = () => api.get("/transactions").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/properties").then((r) => setProps(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", { ...form, amount: Number(form.amount) || 0, property_id: form.property_id || null });
      toast.success("Écriture ajoutée");
      setOpen(false); setForm({ ...form, amount: "", description: "" }); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => { await api.delete(`/transactions/${id}`); load(); };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/transactions/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} transaction(s) importée(s)`);
      load();
    } catch { toast.error("Import échoué — format attendu: date;libellé;montant"); }
    e.target.value = "";
  };

  if (!items) return <Loading />;
  const rev = items.filter((t) => t.type === "revenue").reduce((s, t) => s + t.amount, 0);
  const exp = items.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const cats = form.type === "revenue" ? REV_CATS : EXP_CATS;

  return (
    <div>
      <PageHeader
        title="Comptabilité"
        subtitle="Recettes, charges et import bancaire pour votre régime réel."
        action={
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="csv-input" />
            <Button variant="outline" className="rounded-full gap-2" onClick={() => fileRef.current?.click()} data-testid="import-bank-btn"><Upload className="w-4 h-4" /> Import bancaire</Button>
            <Button className="rounded-full gap-2" onClick={() => setOpen(true)} data-testid="add-transaction-btn"><Plus className="w-4 h-4" /> Écriture</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="p-5 rounded-xl border border-border bg-card"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Recettes</span><TrendingUp className="w-4 h-4 text-[hsl(var(--chart-2))]" /></div><p className="font-heading text-2xl font-bold mt-2" data-testid="acc-revenues">{eur(rev)}</p></div>
        <div className="p-5 rounded-xl border border-border bg-card"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Charges</span><TrendingDown className="w-4 h-4 text-destructive" /></div><p className="font-heading text-2xl font-bold mt-2" data-testid="acc-expenses">{eur(exp)}</p></div>
        <div className="p-5 rounded-xl border border-border bg-card"><span className="text-sm text-muted-foreground">Solde</span><p className={`font-heading text-2xl font-bold mt-2 ${rev - exp >= 0 ? "" : "text-destructive"}`} data-testid="acc-balance">{eur(rev - exp)}</p></div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Aucune écriture" desc="Ajoutez une écriture ou importez un relevé bancaire (CSV : date;libellé;montant)." />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Catégorie</TableHead><TableHead>Description</TableHead><TableHead>Source</TableHead><TableHead className="text-right">Montant</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                  <TableCell className="font-mono-plex text-sm">{dateFr(t.date)}</TableCell>
                  <TableCell>{t.category}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[240px] truncate">{t.description}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px]">{t.source}</Badge></TableCell>
                  <TableCell className={`text-right font-medium ${t.type === "revenue" ? "text-[hsl(var(--chart-2))]" : "text-destructive"}`}>{t.type === "revenue" ? "+" : "−"}{eur(t.amount)}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" className="text-muted-foreground h-8 w-8" onClick={() => remove(t.id)} data-testid={`del-txn-${t.id}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle écriture</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: v === "revenue" ? REV_CATS[0] : EXP_CATS[0] })}>
                  <SelectTrigger data-testid="txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="revenue">Recette</SelectItem><SelectItem value="expense">Charge</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} required data-testid="txn-date" /></div>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="txn-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Montant</Label><Input type="number" step="0.01" value={form.amount} onChange={set("amount")} required data-testid="txn-amount" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
            <DialogFooter><Button type="submit" className="rounded-full" data-testid="save-txn-btn">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
